// Empotage rapport data snapshot — what the PDF generator and any
// future on-screen "Rapport" preview consume. Pure transform off the
// OT row; no React, no jsPDF imports here.
//
// Mirrors the OT-level math already used by the Empotage panel
// (containerNetTheoretical / containerNetReal / lotsTotalKg /
// ecartFreinte) so the document and the panel can never disagree.

import {
  containerNetReal,
  containerNetTheoretical,
  ecartFreinte,
  lotsTotalKg,
  otTotalNetReal,
  otTotalNetTheoretical,
} from "@/features/transit/lib/empotage-math"
import type {
  OT,
  OTContainer,
  PhotoItem,
} from "@/features/transit/api/types"

export interface RapportContainerRow {
  id: number
  containerNumber: string
  plombNumber: string
  stuffingDate: string | null
  // Mode + derived container size — already on the OT row but normalised
  // here so the generator doesn't re-derive.
  empotageMode: string | null
  containerType: string | null
  loadedKg: number | null
  emptyKg: number | null
  theoreticalTareKg: number | null
  realTareKg: number | null
  dressingKg: number | null
  netTheoreticalKg: number | null
  netRealKg: number | null
  observation: string | null
}

export interface RapportRollups {
  engageKg: number
  totalNetTheoreticalKg: number | null
  totalNetRealKg: number | null
  // Both blocks are null when engagé is 0 or netTotal is unset.
  theoretical: { ecart: number; freintePct: number } | null
  real: { ecart: number; freintePct: number } | null
}

export interface EmpotageRapport {
  otNumber: string
  label: string
  clientName: string
  contractNumber: string
  vessel: string | null
  voyage: string | null
  generatedAt: string
  containers: RapportContainerRow[]
  rollups: RapportRollups
  photos: PhotoItem[]
}

export function buildEmpotageRapport(ot: OT): EmpotageRapport {
  // Sort containers by id so the rapport order is stable across edits.
  const containers = [...(ot.containers ?? [])].sort((a, b) => a.id - b.id)

  const engageKg = lotsTotalKg(ot.lots ?? [])
  const totalNetTheoreticalKg = otTotalNetTheoretical(containers)
  const totalNetRealKg = otTotalNetReal(containers)

  return {
    otNumber: ot.otNumber,
    label: ot.label,
    clientName: ot.clientName,
    contractNumber: ot.contractNumber ?? `#${ot.contractId}`,
    vessel: ot.vessel ?? null,
    voyage: ot.voyage ?? null,
    generatedAt: new Date().toISOString(),
    containers: containers.map(rowFromContainer),
    rollups: {
      engageKg,
      totalNetTheoreticalKg,
      totalNetRealKg,
      theoretical: ecartFreinte(engageKg, totalNetTheoreticalKg),
      real: ecartFreinte(engageKg, totalNetRealKg),
    },
    photos: ot.photos ?? [],
  }
}

function rowFromContainer(c: OTContainer): RapportContainerRow {
  const inputs = {
    loadedKg: c.loadedKg ?? null,
    emptyKg: c.emptyKg ?? null,
    theoreticalTareKg: c.theoreticalTareKg ?? null,
    realTareKg: c.realTareKg ?? null,
    dressingKg: c.dressingKg ?? null,
    weighingWeightKg: c.weighingWeightKg ?? null,
  }
  return {
    id: c.id,
    containerNumber: c.containerNumber ?? "",
    plombNumber: c.plombNumber ?? "",
    stuffingDate: c.stuffingDate ?? null,
    empotageMode: c.empotageMode ?? null,
    containerType: c.containerType ?? null,
    loadedKg: c.loadedKg ?? null,
    emptyKg: c.emptyKg ?? null,
    theoreticalTareKg: c.theoreticalTareKg ?? null,
    realTareKg: c.realTareKg ?? null,
    dressingKg: c.dressingKg ?? null,
    netTheoreticalKg: containerNetTheoretical(inputs),
    netRealKg: containerNetReal(inputs),
    observation: c.observation ?? null,
  }
}

// hasAnyWeight reports whether the rapport carries enough data to
// be worth generating. Empty containers / no weights at all → button
// stays disabled so we don't ship a blank PDF.
export function hasAnyWeight(rapport: EmpotageRapport): boolean {
  return rapport.containers.some(
    (c) => c.netTheoreticalKg != null || c.netRealKg != null,
  )
}
